/**
 * HindcastPanel — nightly hindcast results (real-market model scoring).
 *
 * Reads GET /api/hindcast/latest + /history. A hindcast run replays the
 * council on recently-resolved real markets and scores it (Brier / win rate /
 * edge capture). No runs recorded is a real, common state (runs are manually
 * triggered) — render it honestly, never as zero scores.
 */
import { History, FlaskConical } from "lucide-react";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { useHindcastHistory, useHindcastLatest } from "@/sdk/hooks/use-hindcast";

function brierTone(score: number | null): "default" | "success" | "warning" | "danger" {
  if (score === null) return "default";
  if (score <= 0.1) return "success";
  if (score <= 0.25) return "warning";
  return "danger";
}

export function HindcastPanel() {
  const latestQuery = useHindcastLatest();
  const historyQuery = useHindcastHistory(10);

  const run = latestQuery.data?.run ?? null;
  const history = historyQuery.data?.runs ?? [];

  return (
    <Panel
      title="Hindcast"
      description="Council replayed on resolved real markets, scored vs outcome"
    >
      {latestQuery.isError ? (
        <QueryErrorState
          what="hindcast results"
          onRetry={() => void latestQuery.refetch()}
        />
      ) : latestQuery.isLoading ? (
        <LoadingState text="Loading hindcast results..." />
      ) : !run ? (
        <div className="py-6 text-center">
          <FlaskConical className="mx-auto h-8 w-8 text-white/20" />
          <p className="mt-2 text-sm text-white/40">No hindcast runs recorded yet.</p>
          <p className="text-xs text-white/25">
            Runs are triggered via POST /api/hindcast/run (authenticated) and
            appear here once recorded.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <StatCard
              label="Avg Brier"
              value={run.avg_brier !== null ? run.avg_brier.toFixed(3) : "—"}
              tone={brierTone(run.avg_brier)}
              hint="Lower = better"
            />
            <StatCard
              label="Win Rate"
              value={
                run.win_rate !== null ? `${(run.win_rate * 100).toFixed(1)}%` : "—"
              }
              hint={`${run.markets_tested} markets`}
            />
            <StatCard
              label="Edge Capture"
              value={
                run.edge_capture !== null ? run.edge_capture.toFixed(3) : "—"
              }
              hint={`${run.lookback_days}d lookback`}
            />
          </div>
          <div className="flex items-center justify-between text-[0.68rem] text-white/30">
            <span>
              Run {run.run_date}
              {typeof run.markets_failed === "number" && run.markets_failed > 0
                ? ` · ${run.markets_failed} failed`
                : ""}
              {typeof run.markets_degraded === "number" && run.markets_degraded > 0
                ? ` · ${run.markets_degraded} degraded`
                : ""}
            </span>
            <span>{run.model_count ?? "—"} models</span>
          </div>

          {/* A failed history read must not be indistinguishable from
              "only one run exists" (Greptile, PR #1892). */}
          {historyQuery.isError ? (
            <p className="text-[0.7rem] text-amber-400/70">
              History unavailable — backend unreachable
            </p>
          ) : history.length > 1 ? (
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-xs text-white/50">
                <History className="h-3 w-3" />
                Recent runs
              </div>
              <div className="space-y-1">
                {history.map((h) => (
                  <div
                    key={h.id}
                    className="flex items-center justify-between rounded border border-white/5 px-3 py-1.5 text-[0.7rem]"
                  >
                    <span className="text-white/60">{h.run_date}</span>
                    <span className="flex gap-3 font-mono text-white/50">
                      <span>{h.markets_tested} mkts</span>
                      <span>
                        Brier {h.avg_brier !== null ? h.avg_brier.toFixed(3) : "—"}
                      </span>
                      <span>
                        WR{" "}
                        {h.win_rate !== null
                          ? `${(h.win_rate * 100).toFixed(0)}%`
                          : "—"}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  );
}
